'use client';

import { LngLat } from '@yandex/ymaps3-types';
import { clusterByGrid as clusterByGridMethod } from '@yandex/ymaps3-types/packages/clusterer';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useEffect, useState } from 'react';

import { Shop, ShopList } from '@/api/backend/contract/Shop';
import { useMaps } from '@/api/yandex-maps';
import { UiShopPointInfo } from '@/ui/shop-point-info';
import { UiShopPointSelector } from '@/ui/shop-point-selector';
import { UiKitDrawer } from '@/ui-kit/drawer';
import { UiKitMapContent } from '@/ui-kit/map-content';
import { UiKitPanel } from '@/ui-kit/panel';
import { UiKitPanelPeek, UiKitPanelPeekRef } from '@/ui-kit/panel-peek';
import { useMediaQuery } from '@/ui-tool/media-query';
import { DEFAULT_CLUSTER_GRID_SIZE } from '@/util/map-point-maps/consts';
import { useMapController } from '@/util/map-point-maps/hooks';

import { styles } from './UiMapsShop.styles';

interface DefaultProps {
  points: ShopList;
  variant?: 'default';
}

export type UiMapsShopProps = DefaultProps & {
  onPointConfirm?: (pointId: string) => void;
  city?: string;
  /**
   * Флаг для отображения только карты на мобильных устройствах
   */
  isMobileOnlyMap?: boolean;
  center?: LngLat;
  loading?: boolean;
  isFreeShipping?: boolean;
  type?: 'pdp' | 'simple';
  onLinkClick?: (params: { label: string; url: string }) => void;
};

export function UiMapsShop({
  onPointConfirm,
  points,
  isMobileOnlyMap = false,
  center,
  loading = false,
  isFreeShipping,
  type = 'simple',
  onLinkClick,
  ...props
}: UiMapsShopProps) {
  const slots = styles({ type });
  const t = useTranslations();

  const { reactifyApi, clusterer, suggest } = useMaps();

  const {
    location,
    selectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleZoomIn,
    handleZoomOut
  } = useMapController(center);

  const [openDrawer, setOpenDrawer] = useState(false);

  const isDesktop = useMediaQuery('lg');

  const gridSizedMethod = useMemo(() => {
    if (!clusterer) {
      return null;
    }

    const clusterByGrid =
      clusterer.clusterByGrid as unknown as typeof clusterByGridMethod;

    return clusterByGrid({ gridSize: DEFAULT_CLUSTER_GRID_SIZE });
  }, [clusterer]);

  const panelRef = useRef<UiKitPanelPeekRef>(null);

  const handlePointConfirm = (pointId: string) => {
    onPointConfirm?.(pointId);
  };

  const onMapMarkerClickWithDrawer = useCallback(
    (feature: Shop) => {
      const result = handleMarkerClick(true)(feature);

      if (!isDesktop && type === 'simple' && result.isMap) {
        setOpenDrawer(true);
      }
    },
    [handleMarkerClick, isDesktop, setOpenDrawer, type]
  );

  const onSelectPointWithDrawer = useCallback(
    (feature: Shop | null) => {
      if (!feature) return;

      const result = handleMarkerClick(true)(feature);

      if (!isDesktop && type === 'simple' && result.isMap) {
        setOpenDrawer(true);
      }
    },
    [handleMarkerClick, isDesktop, setOpenDrawer, type]
  );

  useEffect(() => {
    if (!selectedPoint) return;

    requestAnimationFrame(() => {
      panelRef.current?.recalculate();
    });
  }, [selectedPoint]);

  if (!reactifyApi || !clusterer || !gridSizedMethod || !suggest) {
    return null;
  }

  const shopsPointSelector = (
    <UiShopPointSelector
      key="shop-point-selector"
      onPointConfirm={handlePointConfirm}
      activePointId={selectedPoint?.id ?? null}
      pointsList={points}
      onSelectPoint={handleMarkerClick(false)}
      hideConfirmButton={!onPointConfirm}
      loading={loading}
      isFreeShipping={isFreeShipping}
      city={type === 'pdp' ? props.city : undefined}
      type={type}
      onLinkClick={onLinkClick}
    />
  );

  const mapNode = (
    <UiKitMapContent
      key="map-content-shop"
      reactifyApi={reactifyApi}
      clusterer={clusterer}
      gridSizedMethod={gridSizedMethod}
      pointsList={points}
      selectedPoint={selectedPoint}
      location={location}
      isDesktop={isDesktop}
      onMarkerClick={(feature) => onMapMarkerClickWithDrawer(feature as Shop)}
      onClusterClick={handleClusterClick}
      type="shops"
      onZoomIn={handleZoomIn}
      onZoomOut={handleZoomOut}
    />
  );

  if (isDesktop) {
    return (
      <div className={slots.base()}>
        <div className={slots.pickupPointSelector()}>{shopsPointSelector}</div>
        <div className={slots.map()}>{mapNode}</div>
      </div>
    );
  }

  const mobileMapNode = <div className={slots.panelContent()}>{mapNode}</div>;

  const shopsPointSelectorMobile = (
    <UiShopPointSelector
      key="shop-point-selector"
      onPointConfirm={handlePointConfirm}
      activePointId={selectedPoint?.id ?? null}
      pointsList={points}
      onSelectPoint={handleMarkerClick(false)}
      hideConfirmButton={!onPointConfirm}
      loading={loading}
      isFreeShipping={isFreeShipping}
      city={type === 'pdp' ? props.city : undefined}
      targetBlank={true}
      type={type}
      onLinkClick={onLinkClick}
    />
  );

  const shopsPointSelectorMobileSimple = (
    <UiShopPointSelector
      key="shop-point-selector"
      onPointConfirm={handlePointConfirm}
      activePointId={selectedPoint?.id ?? null}
      pointsList={points}
      onSelectPoint={(point) => onSelectPointWithDrawer(point as Shop | null)}
      hideConfirmButton={!onPointConfirm}
      loading={loading}
      isFreeShipping={isFreeShipping}
      city={type === 'pdp' ? props.city : undefined}
      type={type}
      isScroll={false}
      onLinkClick={onLinkClick}
    />
  );

  return (
    <>
      {isMobileOnlyMap ? (
        <div className={slots.panelContent()}>{mobileMapNode}</div>
      ) : (
        <>
          {type !== 'simple' && (
            <>
              <div className={slots.panelContent()}>{mobileMapNode}</div>
              <UiKitPanelPeek
                ref={panelRef}
                peekHeight={172}
              >
                {shopsPointSelectorMobile}
              </UiKitPanelPeek>
            </>
          )}

          {type == 'simple' && (
            <>
              <UiKitDrawer
                title=""
                open={openDrawer && !!selectedPoint}
                onOpenChange={setOpenDrawer}
                direction="bottom"
                rounded="top"
              >
                {selectedPoint && (
                  <UiShopPointInfo
                    point={selectedPoint as Shop}
                    activePointId={selectedPoint?.id ?? null}
                    onPointConfirm={handlePointConfirm}
                    hideConfirmButton={!onPointConfirm}
                    isFreeShipping={isFreeShipping}
                    isVisibleStockBtn={type === 'simple'}
                    isDrawer={true}
                    type={type}
                    onSelectPoint={handleMarkerClick(false)}
                    onLinkClick={onLinkClick}
                  />
                )}
              </UiKitDrawer>
              <UiKitPanel
                tabsClassName={slots.tabs()}
                tabClassName={slots.tab()}
                view="tab"
                items={[
                  {
                    header: t('asList'),
                    content: (
                      <div
                        className={slots.panelContent()}
                        key="tab-shop-list"
                      >
                        {shopsPointSelectorMobileSimple}
                      </div>
                    )
                  },
                  {
                    header: t('asMap'),
                    content: (
                      <div
                        className={slots.panelContentMap()}
                        key="tab-shop-map"
                      >
                        {mobileMapNode}
                      </div>
                    )
                  }
                ]}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
